import React, { useEffect, useState } from "react";
/** @jsx jsx */
import { jsx, css, SerializedStyles } from '@emotion/react';
import { Colors } from "../palettes/colorPalette";
import { get } from "http";
import { getLeagueTier, getTierIndex } from "../../../shared/utils";
import { RankPerTier } from "../../../shared/constants";

type position = 'center' | 'top-left' | 'top-right' | 'bottom-right' | 'bottom-center' | 'inline';

type TierMakerProps = {
	position?: position;
	relative?: boolean;
	displayNumber?: boolean;
	tier: number;
	isReward?: boolean;
}

const abs = css`
	position: absolute;
`;

const rel = css`
	position: relative;
`;

const style = css`
	fill: cadetblue;
	height: 1.5vw;
	width: 1.5vw;
	line-height: 20px;
	filter: drop-shadow(2px 4px 2px ${Colors.UICarbShadow})
`;

const center = css`
	top: 50%;
	left: 50%;
    transform: translate(-50%, -50%);
`;

const bottomRight = css`
    bottom: 0;
    right: 0;
`;

const bottomCenter = css`
    top: 90%;
    left: 50%;
	transform: translateX(-50%);
`;

const topRight = css`
    top: 5px;
    right: 0;
`;

const topLeft = css`
    top: 0;
    left: 0;
`;

const none = css` `;

const tierMap = [
	<polygon points="" />,
	<polygon points="366.4213562373095,366.4213562373095 83.57864376269052,366.4213562373095 83.57864376269046,83.57864376269052 366.4213562373095,83.57864376269046" />,
	<polygon points="386.8033988749895,342.55705045849464 163.19660112501055,415.21130325903073 25,225.00000000000003 163.1966011250105,34.7886967409693 386.80339887498945,107.44294954150533" />,
	<polygon points="398.20508075688775,325 225,425 51.794919243112304,325.00000000000006 51.79491924311225,125.00000000000006 224.99999999999997,25 398.20508075688775,125.00000000000007" />,
	<polygon points="409.7759065022574,301.53668647301794 301.536686473018,409.7759065022574 148.46331352698206,409.7759065022574 40.22409349774264,301.536686473018 40.22409349774264,148.46331352698206 148.46331352698195,40.2240934977427 301.536686473018,40.22409349774267 409.77590650225727,148.46331352698192" />
];

const tierConfig = [
	{ //this is the reward tier
		points: "409.7759065022574,301.53668647301794 301.536686473018,409.7759065022574 148.46331352698206,409.7759065022574 40.22409349774264,301.536686473018 40.22409349774264,148.46331352698206 148.46331352698195,40.2240934977427 301.536686473018,40.22409349774267 409.77590650225727,148.46331352698192",
		fill: Colors.UIRewardTier,
	},
	{
		points: "325,398.2050807568877 25,225.00000000000003 324.9999999999999,51.79491924311219",
		fill: Colors.UITier1,
	},
	{
		points: "366.4213562373095,366.4213562373095 83.57864376269052,366.4213562373095 83.57864376269046,83.57864376269052 366.4213562373095,83.57864376269046",
		fill: Colors.UITier2,
	},
	{
		points: "386.8033988749895,342.55705045849464 163.19660112501055,415.21130325903073 25,225.00000000000003 163.1966011250105,34.7886967409693 386.80339887498945,107.44294954150533",
		fill: Colors.UITier3,
	},
	{
		points: "398.20508075688775,325 225,425 51.794919243112304,325.00000000000006 51.79491924311225,125.00000000000006 224.99999999999997,25 398.20508075688775,125.00000000000007",
		fill: Colors.UITier4,
	},
	{
		points: "409.7759065022574,301.53668647301794 301.536686473018,409.7759065022574 148.46331352698206,409.7759065022574 40.22409349774264,301.536686473018 40.22409349774264,148.46331352698206 148.46331352698195,40.2240934977427 301.536686473018,40.22409349774267 409.77590650225727,148.46331352698192",
		fill: Colors.UITier5,
	},
	{
		points: "415.21130325903073,286.80339887498945 342.55705045849464,386.8033988749895 225,425 107.4429495415054,386.8033988749895 34.7886967409693,286.8033988749895 34.78869674096927,163.19660112501055 107.44294954150536,63.196601125010545 224.99999999999997,25 342.5570504584946,63.19660112501049 415.21130325903073,163.1966011250105",
		fill: Colors.UITier6,
	}
]

export const TierMarker: React.FC<TierMakerProps> = ({ position, relative, tier, displayNumber, isReward }) => {
	const text = tier === 0 ? "R" : tier;
	let { points, fill } = tierConfig[tier];
	if (isReward) {
		let rewardShape = tierConfig[0];
		points = rewardShape.points;
		fill = rewardShape.fill;
	}
	const [posStyle, setPosStyle] = useState(bottomRight);

	useEffect(() => {
		switch (position) {
			case 'center':
				setPosStyle(center);
				break;
			case 'top-left':
				setPosStyle(topLeft);
				break;
			case 'top-right':
				setPosStyle(topRight);
				break;
			case 'bottom-right':
				setPosStyle(bottomRight);
				break;
		}
	}, [position]);

	return (
		<svg css={css`${style} ${posStyle} ${relative ? rel : abs}`} viewBox="0 0 450 450">
			<style>{`
				.text {
				font: 225px sans-serif;
				fill: ${Colors.UITierText};
				alignment-baseline: central;
				dominant-baseline: central;
				text-anchor: middle;
				text-shadow: 2px 2px 2px ${Colors.UICarbShadow};
				}
			`}</style>
			<polygon points={points} fill={fill} transform={`rotate(90 225 225)`} />
			{displayNumber && <text x="225" y="225" className="text">{text}</text>}
		</svg>
	);
}

interface CostMarkerProps {
	position?: position;
	cost: number;
}

export const CostMarker: React.FC<CostMarkerProps> = ({ cost, position }) => {
	const [posStyle, setPosStyle] = useState(center);

	useEffect(() => {
		switch (position) {
			case 'center':
				setPosStyle(center);
				break;
			case 'top-left':
				setPosStyle(topLeft);
				break;
			case 'top-right':
				setPosStyle(topRight);
				break;
			case 'bottom-right':
				setPosStyle(bottomRight);
				break;
			case 'bottom-center':
				setPosStyle(bottomCenter);
				break;
			case 'inline':
				setPosStyle(none);
				break;
		}
	}, [position]);

	return (
		<svg css={css`${style} ${posStyle} ${position !== 'inline' && abs}`} viewBox="0 0 450 450">
			<style>{`
				.text {
					font: 225px sans-serif;
					fill: ${Colors.UITierText};
					alignment-baseline: central;
					dominant-baseline: central;
					text-anchor: middle;
					text-shadow: 2px 2px 2px ${Colors.UICarbShadow};
				}

				.rect {
					fill: ${Colors.UICarb};
				}
			`}</style>
			<circle fill={Colors.UICarb} cx={225} cy={225} r={200} />
			<circle cx={225} cy={225} r={120} fill={Colors.UICarbInner} />
			<text x="225" y="225" className="text">{cost}</text>
		</svg>
	);
}

// Making a new component since there's not enough overlap
interface FreeMarkerProps {
	position?: position;
}
export const FreeMarker: React.FC<FreeMarkerProps> = ({ position }) => {
	const [posStyle, setPosStyle] = useState(center);

	useEffect(() => {
		switch (position) {
			case 'center':
				setPosStyle(center);
				break;
			case 'top-left':
				setPosStyle(topLeft);
				break;
			case 'top-right':
				setPosStyle(topRight);
				break;
			case 'bottom-right':
				setPosStyle(bottomRight);
				break;
		}
	}, [position]);

	return (
		<svg css={css`${style} ${posStyle} ${abs}
			height: 1.5vw;
			width: 2vw;
		`} viewBox="0 0 800 450">
			<style>{`
				.text {
					font: 225px sans-serif;
					font-weight: bold;
					fill: ${Colors.UITierText};
					alignment-baseline: central;
					dominant-baseline: central;
					text-anchor: middle;
					text-shadow: 2px 2px 2px ${Colors.UICarbShadow};
				}

				.rect {
					fill: ${Colors.UITier1};
				}
			`}</style>
			<rect className="rect" x="0" y="0" width="800" height="450" rx="100" ry="100" />
			<text x="400" y="225" className="text">FREE</text>
		</svg>
	);
}

interface LeagueMarkerProps {
	position?: position;
	rank: number;
}
export const LeagueMarker: React.FC<LeagueMarkerProps> = ({ position, rank }) => {
	const tier = getLeagueTier(rank);
	let { points, fill } = tierConfig[tier];
	const [posStyle, setPosStyle] = useState(bottomRight);

	useEffect(() => {
		switch (position) {
			case 'center':
				setPosStyle(center);
				break;
			case 'top-left':
				setPosStyle(topLeft);
				break;
			case 'top-right':
				setPosStyle(topRight);
				break;
			case 'bottom-right':
				setPosStyle(bottomRight);
				break;
		}
	}, [position]);

	return (
		<svg css={css`${style} ${posStyle}`} viewBox="0 0 450 450">
			<style>{`
				.text {
				font: 225px sans-serif;
				fill: ${Colors.UITierText};
				alignment-baseline: central;
				dominant-baseline: central;
				text-anchor: middle;
				text-shadow: 2px 2px 2px ${Colors.UICarbShadow};
				}
			`}</style>
			<polygon points={points} fill={fill} transform={`rotate(90 225 225)`} />
			<text x="225" y="225" className="text">{tier}</text>
		</svg>
	);
}

interface RemainingMarkerProps {
	rank: number;
}
export const RemainingMarker: React.FC<RemainingMarkerProps> = ({ rank }) => {
	const index = getTierIndex(rank);

	//Top tier, there are no more wins to count towards. (Not getLeagueTier, which is also 0 for the bottom tier)
	if (index >= RankPerTier.length - 1) {
		return <></>;
	}

	const remaining = RankPerTier[index + 1] - RankPerTier[index];
	const remainingWins = RankPerTier[index + 1] - rank;

	const circles = Array.from({ length: remaining }, (_, i) =>
		<circle
			key={i}
			fill={remainingWins <= i ? Colors.UICarb : Colors.UICarbSpent}
			cx={225 + i * 425}
			cy={225}
			r={200}
		/>
	);

	return (
		<svg css={css`
			${style}
			width: 3.5vw;
		`} viewBox={`0 0 ${(remaining + 1) * 425} 450`}>
			{circles}
		</svg>
	);
}
